// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { getProviderConfig } from '../../api/config';
import { getAuthStatus, type AuthStatus } from '../../api/auth';
import { useBranding } from '../../contexts/BrandingContext';
import type { ProviderConfig } from '../../types';

export function Layout() {
  const [config, setConfig] = useState<ProviderConfig | null>(null);
  const [user, setUser] = useState<AuthStatus | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { branding, setProviderCode } = useBranding();

  useEffect(() => {
    getProviderConfig()
      .then((cfg) => {
        setConfig(cfg);
        // Set the provider code so BrandingContext can fetch branding
        setProviderCode(cfg.providerCode);
      })
      .catch(() => {});
    getAuthStatus()
      .then(setUser)
      .catch(() => {});
  }, [setProviderCode]);

  // Resolve logo and name: branding takes precedence, then config fallback
  const logoUrl = branding.logoUrl !== '/logo.svg' ? branding.logoUrl : config?.logoUrl;
  const providerName = config?.displayName ?? config?.providerName;

  return (
    <div className="flex h-full" style={{ backgroundColor: 'var(--color-bg)' }}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - hidden on mobile unless open */}
      <div
        className={[
          'fixed inset-y-0 left-0 z-30 lg:static lg:z-auto transition-transform duration-200',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        ].join(' ')}
      >
        <Sidebar
          providerName={providerName}
          logoUrl={logoUrl}
        />
      </div>

      {/* Main content area */}
      <div className="flex flex-col flex-1 min-w-0">
        <Header
          providerName={providerName}
          userName={user?.name ?? user?.username}
          userRole={user?.role}
        />

        {/* Mobile menu toggle */}
        <button
          className="absolute top-5 left-4 z-10 p-1.5 rounded-md hover:bg-gray-100 lg:hidden"
          style={{ color: 'var(--color-text-tertiary)' }}
          onClick={() => setSidebarOpen((prev) => !prev)}
          aria-label="Toggle sidebar"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <main className="flex-1 overflow-auto" style={{ padding: 'var(--space-xl)' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
